import React from 'react';
import {
  Box,
  Button,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { FilterAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DEFAULT_FILTERS = {
  snap_mode: 'conservative',
  min_cap_score: 2,
  limit: 20,
};

const denseFieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
};

const selectSx = {
  ...denseFieldSx,
  '& .MuiSelect-select': { fontSize: '0.78rem' },
};

const SignalFilters = ({ filters, onChange }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [draft, setDraft] = React.useState(DEFAULT_FILTERS);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setDraft({
      snap_mode: filters.snap_mode ?? DEFAULT_FILTERS.snap_mode,
      min_cap_score: filters.min_cap_score ?? DEFAULT_FILTERS.min_cap_score,
      limit: filters.limit ?? DEFAULT_FILTERS.limit,
    });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const patchDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onChange({ ...filters, ...draft });
    handleClose();
  };

  const handleReset = () => {
    setDraft({ ...DEFAULT_FILTERS });
  };

  const activeCount = [
    filters.snap_mode !== DEFAULT_FILTERS.snap_mode,
    filters.min_cap_score !== DEFAULT_FILTERS.min_cap_score,
    filters.limit !== DEFAULT_FILTERS.limit,
  ].filter(Boolean).length;

  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FilterAlt sx={{ fontSize: 16 }} />}
        onClick={handleOpen}
        sx={{
          borderRadius: '4px',
          borderColor: open || activeCount > 0 ? '#6366f1' : '#e2e8f0',
          color: open || activeCount > 0 ? '#6366f1' : '#64748b',
          fontWeight: 600,
          fontSize: 13,
          textTransform: 'none',
          '&:hover': {
            borderColor: '#6366f1',
            color: '#6366f1',
            backgroundColor: 'rgba(99,102,241,0.04)',
          },
          '& .MuiButton-startIcon': { mr: 0.5 },
        }}
      >
        {t('reversalCatcher.filters.title')}
        {activeCount > 0 && (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              px: 0.6,
              py: 0.05,
              borderRadius: 10,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: '#6366f1',
              color: '#fff',
              lineHeight: 1.35,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              border: '1px solid #e5e7eb',
              borderRadius: 1,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Typography sx={{
          px: 1.5,
          py: 0.75,
          fontSize: '0.62rem',
          fontWeight: 600,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          bgcolor: '#fafbfc',
          borderBottom: '1px solid #eef2f7',
        }}>
          {t('reversalCatcher.filters.title')}
        </Typography>

        <Box sx={{ px: 1.5, pt: 1.25, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <FormControl fullWidth size="small" sx={selectSx}>
            <InputLabel>{t('reversalCatcher.filters.snapMode')}</InputLabel>
            <Select
              value={draft.snap_mode}
              label={t('reversalCatcher.filters.snapMode')}
              onChange={(e) => patchDraft('snap_mode', e.target.value)}
            >
              <MenuItem value="aggressive">{t('reversalCatcher.filters.aggressive')}</MenuItem>
              <MenuItem value="conservative">{t('reversalCatcher.filters.conservative')}</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type="number"
              label={t('reversalCatcher.filters.minCapScoreShort')}
              value={draft.min_cap_score}
              onChange={(e) => patchDraft('min_cap_score', parseInt(e.target.value, 10) || 1)}
              slotProps={{ htmlInput: { min: 1, max: 3 } }}
              sx={{ ...denseFieldSx, width: 120, flex: '0 0 auto' }}
            />
            <TextField
              size="small"
              type="number"
              label={t('reversalCatcher.filters.limitShort')}
              value={draft.limit}
              onChange={(e) => patchDraft('limit', parseInt(e.target.value, 10) || 5)}
              slotProps={{ htmlInput: { min: 5, max: 100 } }}
              sx={{ ...denseFieldSx, flex: 1, minWidth: 0 }}
            />
          </Box>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 0.75,
          px: 1.5,
          py: 1.25,
          bgcolor: '#fafbfc',
          borderTop: '1px solid #eef2f7',
        }}>
          <Button
            size="small"
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              fontSize: '0.76rem',
              fontWeight: 600,
              color: '#64748b',
            }}
          >
            {t('common.reset')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontSize: '0.76rem',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
            }}
          >
            {t('common.close')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disableElevation
            sx={{
              textTransform: 'none',
              fontSize: '0.76rem',
              fontWeight: 600,
              bgcolor: '#6366f1',
              '&:hover': { bgcolor: '#4f46e5' },
            }}
          >
            {t('common.apply')}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default SignalFilters;
